import { useState, useEffect, useCallback } from 'react';
import { timeToSeconds } from './util.js';
// Constants
const SCRIPT_ID_BASE = 'lds-kaltura-player-script';
const SCRIPT_SRC_BASE = 'https://cdnapisec.kaltura.com/p/';

// For more information on the Kaltura Player API, see:
// https://developer.kaltura.com/player/web/getting-started-web
export function useAddKalturaScript({
  videoId,
  partnerId,
  uiConfId,
  targetDiv,
  chromeless,
  autoplay = false,
  startTime = 0,
}) {
  const SCRIPT_ID = `${SCRIPT_ID_BASE}_${videoId}`;
  const [hasError, setHasError] = useState({
    errorDetected: false,
    errorMessage: '',
  });
  const [kalturaPlayer, setKalturaPlayer] = useState(null);

  useEffect(() => {
    const existingScript = document.getElementById(SCRIPT_ID);
    const newScript = createNewScript(partnerId, uiConfId, SCRIPT_ID);

    const handleError = (event) => {
      const error = event.payload;
      const errorMessage = error.data.data.message || 'Error loading Kaltura player';
      setHasError({ errorDetected: true, errorMessage });
    };

    if (!existingScript) {
      newScript.onload = () => {
        setKalturaPlayer(
          initKalturaPlayer({
            videoId,
            partnerId,
            uiConfId,
            targetDiv,
            chromeless,
            setHasError,
            autoplay,
            startTime,
          })
        );
      };
      document.head.appendChild(newScript);
    }
    // Cleanup function to remove event listener
    return () => {
      if (kalturaPlayer) {
        kalturaPlayer.removeEventListener(
          kalturaPlayer.Event.ERROR,
          handleError
        );
      }
      document.head.removeChild(newScript);
    };
  }, [videoId, partnerId, uiConfId, targetDiv, SCRIPT_ID, chromeless, autoplay, startTime, kalturaPlayer]);

  const seekToPositionKaltura = useCallback(
    (seconds) => {
      if (kalturaPlayer) {
        kalturaPlayer.currentTime = seconds;
        kalturaPlayer.play();
      }
    },
    [kalturaPlayer]
  );

  return { hasError, seekToPositionKaltura };
}

function createNewScript(partnerId, uiConfId, scriptId) {
  const newScript = document.createElement('script');
  newScript.setAttribute('id', scriptId);
  newScript.setAttribute('src', generateScriptSrc(partnerId, uiConfId));
  return newScript;
}

function generateScriptSrc(partnerId, uiConfId) {
  return `${SCRIPT_SRC_BASE}${partnerId}/embedPlaykitJs/uiconf_id/${uiConfId}`;
}

function initKalturaPlayer({
  videoId,
  partnerId,
  uiConfId,
  targetDiv,
  chromeless,
  setHasError,
  autoplay = false,
  startTime = 0,
}) {
  const videoPlayer = getVideoElement(targetDiv);

  if (chromeless) {
    videoPlayer.classList.add('chromeless');
  }

  const kalturaPlayer = setupKalturaPlayer({
    targetDiv,
    partnerId,
    uiConfId,
    autoplay,
    startTime: timeToSeconds(startTime),
  });
  kalturaPlayer.loadMedia({ entryId: videoId });
  kalturaPlayer.addEventListener(kalturaPlayer.Event.ERROR, (event) => {
    const error = event.payload;

    const errorMessage = error.data.data.message
      ? `${error.data.data.message}. Check the console for more details.`
      : 'Error loading Kaltura player. Check the console for more details.';

    setHasError({ errorDetected: true, errorMessage });
  });
  return kalturaPlayer;
}

function getVideoElement(targetDiv) {
  return document.getElementById(targetDiv);
}

function setupKalturaPlayer({
  targetDiv,
  partnerId,
  uiConfId,
  autoplay,
  startTime,
}) {
  // eslint-disable-next-line no-undef
  return KalturaPlayer.setup({
    targetId: targetDiv,
    provider: {
      partnerId,
      uiConfId,
    },
    playback: {
      autoplay,
    },
    plugins: {
      'playkit-js-info': {},
    },
    sources: {
      startTime,
    },
  });
}
